import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { fetch } from '@ngrx/router-store/data-persistence';
import { Store } from '@ngrx/store';
import { has, toLower, trim } from 'lodash';
import {
  catchError,
  filter,
  first,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  AuthFeatureActions,
  CreateAccountActions,
  LoginActions,
} from '@lockton-apps/auth/action';
import { ProposalHomeActions } from '@lockton-apps/proposals/action';
import { AuthApiActions } from '@lockton-apps/shared/action/auth';
import { NavActions } from '@lockton-apps/shared/action/misc';
import { QuotesApiActions } from '@lockton-apps/shared/action/quotes';
import {
  AuthErrorCodes,
  OtpSession,
  QUOTE_STATUSES,
  Role,
  Session,
} from '@lockton-apps/shared/interfaces';
import {
  FeedbackService,
  NotificationBarService,
} from '@lockton-apps/shared/ui/lui';
import { FreshdeskService } from '@lockton-apps/shared/util/freshdesk';
import { LocalizationService } from '@lockton-apps/shared/util/localization';
import { getProposalUrl } from '@lockton-apps/shared/util/proposal-utils';

import { AuthService } from '../services/auth.service';
import * as AuthActions from './auth.actions';
import { getRole } from './auth.selectors';

const authMessages = {
  loginSuccess: (name: string) =>
    $localize`:@@login-success:Welcome back ${name}`,
  loginFailure: () =>
    $localize`:@@login-failure:Invalid username or password, please try again`,
  signupFailure: () =>
    $localize`:@@signup-failure:We are unable to create your account at this time, if you already have an account you can login <a class="lui-link" href="/">here</a>.\nFor any other issues please contact your Lockton Consultant`,
  setPasswordFailure: () =>
    $localize`:@@token-expired-failure:Your token has expired, please request a new one`,
  changePasswordSuccess: () =>
    $localize`:@@change-password-success:Your password has been updated!`,
  resend2faCode: () =>
    $localize`:@@resend-2fa-code:Your verification code has been resent. Please check your email`,
  resend2faCodeFailure: () =>
    $localize`:@@resend-2fa-code-failure:Something has gone wrong. Please try again`,
  twoFactorAuthFailure: () =>
    $localize`:@@2fa-auth-failure:Invalid or expired code. Please "Resend code" and try again`,
  initSuccess: (name: string) =>
    $localize`:@@init-success:Welcome back ${name}`,
  logoutSuccess: () =>
    $localize`:@@logout-success:You have been logged out successfully.`,
};

@Injectable()
export class AuthEffects {
  private navigated$ = this.actions$.pipe(ofType(ROUTER_NAVIGATED));

  init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.init),
      map(() => {
        const session = this.authService.init();

        return session
          ? AuthActions.initSuccess({ session })
          : AuthActions.initFailure();
      })
    )
  );

  initSuccessThenRefreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.initSuccess),
      fetch({
        run: () => {
          return this.authService
            .refreshToken()
            .pipe(
              map((res) => AuthActions.refreshTokenSuccess({ token: res }))
            );
        },
        onError: (error) => {
          return AuthActions.refreshTokenFailure({
            message: 'authentication failed',
          });
        },
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.login),
      switchMap(({ authDetails }) => {
        return this.authService.authenticate(authDetails).pipe(
          map((res) => {
            if (this.isOtpSession(res)) {
              return AuthFeatureActions.requireTwoFactorAuth();
            } else if (this.isSession(res)) {
              return AuthApiActions.loginSuccess({ session: res });
            } else {
              return AuthApiActions.loginFailure({
                message: 'login failed',
              });
            }
          }),
          catchError(({ error }: HttpErrorResponse) =>
            of(
              AuthApiActions.loginFailure({
                message: error.errorMessage,
                code: error.errorCode,
              })
            )
          )
        );
      })
    )
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateAccountActions.formSubmitted),
      switchMap(({ signUpDetails }) => {
        return this.authService.signUp(signUpDetails).pipe(
          map((res) => AuthApiActions.signUpSuccess({ session: res })),
          catchError((error) =>
            of(AuthApiActions.signUpFailure({ message: error }))
          )
        );
      })
    )
  );

  requireTwoFactorAuth$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthFeatureActions.requireTwoFactorAuth),
        tap(() => {
          this.router.navigate(['verification']);
        })
      ),
    { dispatch: false }
  );

  twoFactorAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthFeatureActions.twoFactorAuth),
      fetch({
        run: ({ twoFactorDetails }) => {
          return this.authService
            .twoFactorAuthenticate(twoFactorDetails)
            .pipe(map((res) => AuthApiActions.loginSuccess({ session: res })));
        },
        onError: (error) => {
          return AuthApiActions.twoFactorAuthFailure({
            message: authMessages.twoFactorAuthFailure(),
          });
        },
      })
    )
  );

  navigateToCallingUrlOrHome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.loginSuccess, AuthApiActions.signUpSuccess),
        tap(() => {
          if (this.authService.callingUrl) {
            this.router.navigateByUrl(this.authService.callingUrl);
            this.authService.resetCallingUrl();
          } else {
            this.router.navigate(['/']);
          }
        })
      ),
    { dispatch: false }
  );

  setLocale$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.loginSuccess, AuthApiActions.signUpSuccess),
        tap(({ session }) => {
          // #TODO handle elsewhere
          if (
            toLower(session.language) !==
            toLower(this.localizationService.getDocumentLocale())
          ) {
            localStorage.setItem('showWelcome', 'true');
          }

          this.localizationService.setLocale(trim(session.language));
        })
      ),
    { dispatch: false }
  );

  loginFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.loginFailure),
        filter(({ code }) => code !== AuthErrorCodes.AuthCredentialsExpired),
        tap(() => {
          this.feedbackService.subtleMessage(authMessages.loginFailure(), {
            type: 'error',
          });
        })
      ),
    { dispatch: false }
  );

  twoFactorAuthFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.twoFactorAuthFailure),
        tap(({ message }) => {
          this.feedbackService.subtleMessage(message, {
            type: 'error',
          });
        })
      ),
    { dispatch: false }
  );

  resend2faCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthFeatureActions.resend2faCode),
      fetch({
        run: ({ email }) => {
          return this.authService.resend2faCode().pipe(
            map(() =>
              AuthApiActions.resend2faCodeSuccess({
                message: authMessages.resend2faCode(),
              })
            )
          );
        },
        onError: (error) => {
          return AuthApiActions.resend2faCodeFailure({
            message: authMessages.resend2faCodeFailure(),
          });
        },
      })
    )
  );

  resend2faCodeFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.resend2faCodeFailure),
        tap(({ message }) => {
          this.feedbackService.subtleMessage(message, {
            type: 'error',
          });
        })
      ),
    { dispatch: false }
  );

  setupCrossTabSessionTerminatedCommunication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.loginSuccess, AuthActions.initSuccess),
      switchMap(() =>
        this.authService.sessionChangeListener().pipe(
          filter((session) => !session),
          map(() => {
            return AuthActions.sessionTerminated();
          })
        )
      )
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthFeatureActions.forgotPassword),
      fetch({
        run: ({ email }) => {
          return this.authService
            .forgotPassword(email)
            .pipe(map(() => AuthApiActions.forgotPasswordSuccess()));
        },
        onError: (error) => {
          return AuthApiActions.forgotPasswordFailure({
            message: 'Forgot password failed',
          });
        },
      })
    )
  );

  setPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthFeatureActions.setPassword),
      switchMap(({ resetPasswordBody }) =>
        this.authService.setPassword(resetPasswordBody).pipe(
          map(() => AuthApiActions.setPasswordSuccess()),
          catchError((error) => of(AuthApiActions.setPasswordFailure(error)))
        )
      )
    )
  );

  setPasswordFailureMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.setPasswordFailure),
        tap(() => {
          this.feedbackService.subtleMessage(
            authMessages.setPasswordFailure(),
            {
              type: 'error',
            }
          );
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AuthActions.logout,
        NavActions.userMenuLogout,
        AuthActions.sessionTerminated
      ),
      switchMap(() => {
        return this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError((error) => of(AuthActions.logoutFailure({ message: '' })))
        );
      })
    )
  );

  logoutSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() =>
          this.feedbackService.subtleMessage(authMessages.logoutSuccess(), {
            type: 'success',
          })
        )
      ),
    { dispatch: false }
  );

  logoutClearNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        tap(() => this.notificationBarService.closeAll())
      ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      fetch({
        run: () => {
          return this.authService
            .refreshToken()
            .pipe(
              map((res) => AuthActions.refreshTokenSuccess({ token: res }))
            );
        },
        onError: (error) => {
          return AuthActions.refreshTokenFailure({
            message: 'refresh token failed',
          });
        },
      })
    )
  );

  bootFreshdesk$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.initSuccess, AuthApiActions.loginSuccess),
        filter(({ session }) => session.role !== Role.Contact),
        switchMap(({ session }) =>
          this.actions$.pipe(
            ofType(ROUTER_NAVIGATED),
            first(),
            tap(() => {
              this.freshdesk.show({
                name: session.given_name,
                email: session.email,
              });
            })
          )
        )
      ),
    { dispatch: false }
  );

  hideFreshdesk$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          this.freshdesk.hide();
        })
      ),
    { dispatch: false }
  );

  contactAuthenticated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthApiActions.loginSuccess, AuthActions.initSuccess),
      withLatestFrom(this.store.select(getRole), (_, role) => role),
      filter((role) => role === Role.Contact),
      map(() => AuthApiActions.contactAuthenticated())
    )
  );

  navigateToProposals$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.signUpSuccess),
        switchMap(() =>
          this.actions$.pipe(
            ofType(QuotesApiActions.loadQuotesSuccess),
            takeUntil(
              this.actions$.pipe(ofType(ProposalHomeActions.beginProposal))
            ),
            filter(
              ({ quotes }) =>
                quotes.length === 1 &&
                quotes[0].status === QUOTE_STATUSES.FormInitialized
            ),
            tap(({ quotes }) => {
              const quote = quotes[0];
              this.router.navigate(getProposalUrl(quote));
            })
          )
        )
      ),
    { dispatch: false }
  );

  // Messages

  showWelcomeBackMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.loginSuccess),
        tap(({ session }) => {
          this.feedbackService.subtleMessage(
            authMessages.loginSuccess(session.given_name),
            {
              type: 'success',
            }
          );
        })
      ),
    { dispatch: false }
  );

  signUpFailureMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.signUpFailure),
        tap(() => {
          this.feedbackService.subtleMessage(authMessages.signupFailure(), {
            type: 'error',
            duration: 15000,
          });
        })
      ),
    { dispatch: false }
  );

  initSuccessThenShowWelcome$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.initSuccess),
        tap(({ session }) => {
          if (localStorage.getItem('showMessage') === 'true') {
            this.feedbackService.subtleMessage(
              authMessages.initSuccess(session.given_name),
              {
                type: 'success',
              }
            );
          }
        })
      ),
    { dispatch: false }
  );

  resend2faCodeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.resend2faCodeSuccess),
        tap(({ message }) => {
          this.feedbackService.subtleMessage(message, {
            type: 'success',
          });
        })
      ),
    { dispatch: false }
  );

  changePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthApiActions.changePasswordSuccess),
        tap(() => {
          this.feedbackService.subtleMessage(
            authMessages.changePasswordSuccess(),
            {
              type: 'success',
            }
          );

          this.router.navigate(['/my-account']);
        })
      ),
    { dispatch: false }
  );
  constructor(
    private readonly store: Store,
    private readonly actions$: Actions,
    private authService: AuthService,
    private feedbackService: FeedbackService,
    private router: Router,
    private localizationService: LocalizationService,
    private notificationBarService: NotificationBarService,
    private freshdesk: FreshdeskService
  ) {}

  private isOtpSession(session: Session | OtpSession): session is OtpSession {
    return has(session, 'requiresMfa');
  }
  private isSession(session: Session | OtpSession): session is Session {
    return !this.isOtpSession(session);
  }
}
